import React, { useState, useMemo } from "react";
import { FiDownload, FiCalendar, FiX } from "react-icons/fi";
import { Table } from "./";
import { IotmateButton, IotmateInput } from "../Shared";
import "./DataHistoryTable.css";

const DataHistoryTable = ({
  data = [],
  columns = [],
  title = "Data History",
  dateField = "timestamp", // Field name in data that contains the date
  enableDateFilter = true,
  enableDownload = true,
}) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Filter data based on date range
  const filteredData = useMemo(() => {
    if (!enableDateFilter || (!startDate && !endDate)) {
      return data;
    }

    return data.filter((row) => {
      const rowDate = new Date(row[dateField]);

      if (isNaN(rowDate.getTime())) {
        return true; // Include rows with invalid dates
      }

      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && end) {
        return rowDate >= start && rowDate <= end;
      } else if (start) {
        return rowDate >= start;
      } else if (end) {
        return rowDate <= end;
      }

      return true;
    });
  }, [data, startDate, endDate, dateField, enableDateFilter]);

  // Download filtered data as CSV
  const downloadCSV = () => {
    if (filteredData.length === 0) {
      alert("No data to download");
      return;
    }

    // Create CSV headers
    const headers = columns.map((col) => col.Header).join(",");

    // Create CSV rows
    const rows = filteredData.map((row) =>
      columns.map((col) => {
        const value = row[col.accessor];
        // Escape values that contain commas or quotes
        if (value && (value.toString().includes(",") || value.toString().includes('"'))) {
          return `"${value.toString().replace(/"/g, '""')}"`;
        }
        return value || "";
      }).join(",")
    );

    // Combine headers and rows
    const csv = [headers, ...rows].join("\n");

    // Create blob and download
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${title.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Clear all filters
  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
  };

  const hasActiveFilters = startDate || endDate;

  return (
    <div className="data-history-table-container">
      {/* Header with filters and download */}
      {(enableDateFilter || enableDownload) && (
        <div className="data-history-filters">
          {/* Date Filters */}
          {enableDateFilter && (
            <div className="date-filter-group">
              <IotmateInput
                type="date"
                placeholder="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                icon={<FiCalendar size={18} />}
                label="From"
                width="200px"
              />
              <IotmateInput
                type="date"
                placeholder="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                icon={<FiCalendar size={18} />}
                label="To"
                width="200px"
              />
              {hasActiveFilters && (
                <IotmateButton
                  variant="secondary"
                  size="medium"
                  onClick={clearFilters}
                  className="clear-filters-btn"
                >
                  <FiX size={18} />
                  Clear Filters
                </IotmateButton>
              )}
            </div>
          )}

          {/* Download Button */}
          {enableDownload && (
            <IotmateButton
              variant="success"
              size="medium"
              onClick={downloadCSV}
              className="download-csv-btn"
            >
              <FiDownload size={18} />
              Download CSV
            </IotmateButton>
          )}
        </div>
      )}

      {/* Info text showing filtered results */}
      {hasActiveFilters && (
        <div className="filter-info">
          Showing {filteredData.length} of {data.length} records
          {startDate && ` from ${new Date(startDate).toLocaleDateString()}`}
          {endDate && ` to ${new Date(endDate).toLocaleDateString()}`}
        </div>
      )}

      {/* Render the Table component */}
      <Table
        columns={columns}
        data={filteredData}
        heading={title}
        typeComplex={true}
      />
    </div>
  );
};

export default DataHistoryTable;
